/**
 * A series, drawn as more than one book.
 *
 * The front cover stays whole and full size because that is what a reader
 * recognises — the same reason every list here leads with a thumbnail. The
 * others step out behind it. A 2×2 mosaic was the alternative and loses on
 * exactly that point: it says "a collection" clearly and *which* collection
 * not at all, because no cover dominates.
 *
 * The empty slot is the part worth being careful about. `Thumb`'s fallback
 * mark means "we expected a picture and have none" — a dead URL, a gap in the
 * catalogue, our failure. This blank means the opposite: there is nothing to
 * picture yet, and that is the news. So it is an outline and nothing else,
 * never the drawn mark, or the two states become indistinguishable.
 */

import { useState } from 'react';
import type { Medium, WatchableKind } from '../../poller/types.js';
import { Thumb } from './Thumb.tsx';

interface Props {
  /** Front first. */
  covers: readonly string[];
  blank: boolean;
  title: string;
  kind: WatchableKind;
  medium: Medium;
}

export function SeriesFan({ covers, blank, title, kind, medium }: Props) {
  // Covers go missing one at a time, and a hole in a fan looks like a bug in
  // the fan. A dead layer is dropped and the rest close up, which is why this
  // tracks failures by URL rather than by index — the indices shift.
  const [dead, setDead] = useState<readonly string[]>([]);
  const alive = covers.filter((src) => !dead.includes(src));

  // Every cover died. Nothing left to fan, and the empty slot alone would be
  // claiming "one book, and another coming" without showing the one book.
  if (alive.length === 0) {
    return <Thumb src={null} kind={kind} medium={medium} size="lg" />;
  }

  // Re-fanned down to a single layer. An outline beside one cover still says
  // "and another", so it survives; a lone cover is just a thumbnail.
  const layers = alive.length + (blank && alive.length < 2 ? 1 : 0);
  if (layers < 2) return <Thumb src={alive[0] ?? null} kind={kind} medium={medium} size="lg" />;

  return (
    <span
      className="fan"
      style={{ '--layers': layers } as React.CSSProperties}
      role="img"
      aria-label={`Covers from ${title}`}
    >
      {alive.map((src, i) => (
        <img
          key={src}
          className={`thumb fan-layer fan-${i}`}
          src={src}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          onError={() => setDead((d) => (d.includes(src) ? d : [...d, src]))}
        />
      ))}
      {blank && alive.length < 2 && (
        <span className={`thumb fan-layer fan-blank fan-${alive.length}`} aria-hidden="true" />
      )}
    </span>
  );
}
